import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DocsPanel from "@/components/oracle/DocsPanel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, BookOpenText, History, Send } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = ["What are the duties of a Frigate Captain?", "How are dues collected and when are they due?", "What happens if a brother misses meetings?", "How is a Visiting Brother received?"];

// Highlights [Document §n] citations in gold
function Answer({ text }) {
  const parts = text.split(/(\[[^\]\n]{2,80}§\s?\d+\])/g);
  return <p className="whitespace-pre-wrap text-sm leading-relaxed sm:text-base" data-testid="oracle-answer">{parts.map((p, i) => (/^\[.*§\s?\d+\]$/.test(p) ? <span key={i} className="rounded bg-[hsl(var(--gold))]/15 px-1 font-mono-data text-[0.75em] text-[hsl(var(--gold))]">{p}</span> : p))}</p>;
}

export default function Oracle() {
  const [q, setQ] = useState("");
  const [asked, setAsked] = useState("");
  const [answer, setAnswer] = useState("");
  const [sources, setSources] = useState([]);
  const [asking, setAsking] = useState(false);
  const [history, setHistory] = useState([]);
  const [docs, setDocs] = useState({ docs: [], can_manage: false, ready: true });
  const endRef = useRef();

  const loadDocs = () => api.get("/oracle/docs").then((r) => setDocs(r.data)).catch(() => {});
  const loadHistory = () => api.get("/oracle/history").then((r) => setHistory(r.data.history)).catch(() => {});
  useEffect(() => { loadDocs(); loadHistory(); }, []);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || asking) return;
    setAsking(true); setAnswer(""); setSources([]); setAsked(text); setQ(text);
    try {
      const res = await fetch(`${API}/oracle/ask`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ question: text }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(typeof d.detail === "string" ? d.detail : "The Oracle is unavailable right now"); }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf("\n\n")) >= 0) {
          const line = buf.slice(0, i).trim(); buf = buf.slice(i + 2);
          if (!line.startsWith("data:")) continue;
          const ev = JSON.parse(line.slice(5));
          if (ev.type === "sources") setSources(ev.sources);
          else if (ev.type === "delta") setAnswer((a) => a + ev.text);
        }
      }
      loadHistory();
    } catch (e) { toast.error(e.message); } finally { setAsking(false); }
  };

  return (
    <div data-testid="oracle-page">
      <PageHeader eyebrow="Rules & Regulations" title="Oracle Boot" description="Ask anything about the Galleon's Rules & Regulations — answers in seconds, strictly from the governing documents uploaded by Galleon admins. Nothing invented." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card className="gold-ring glass p-5" data-testid="oracle-ask-card">
            <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Ask the Oracle</h3>{!docs.docs.length && <Badge variant="outline" className="ml-auto border-amber-500/50 text-amber-500" data-testid="oracle-no-docs">No rules uploaded yet</Badge>}</div>
            <Textarea value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} placeholder="e.g. What is the quorum for a frigate meeting?" className="mt-3 min-h-[88px] resize-none" maxLength={600} data-testid="oracle-question-input" />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => ask(s)} disabled={asking} className="rounded-full border border-[hsl(var(--gold))]/30 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-[hsl(var(--gold))] hover:text-foreground" data-testid="oracle-suggestion">{s}</button>)}
              <Button onClick={() => ask()} disabled={asking || !q.trim()} className="ml-auto bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="oracle-ask-button">{asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" />Ask</>}</Button>
            </div>
          </Card>

          {(asked || asking) && (
            <Card className="gold-ring glass p-5" data-testid="oracle-answer-card">
              <p className="label-eyebrow">You asked</p>
              <p className="mt-1 font-display text-lg font-700" data-testid="oracle-asked">{asked}</p>
              <div className="mt-4 rounded-xl border border-[hsl(var(--gold))]/25 bg-background/50 p-4">
                {answer ? <Answer text={answer} /> : <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--gold))]" />Consulting the Rules & Regulations…</p>}
                {asking && answer && <span className="ml-1 inline-block h-4 w-1.5 animate-pulse bg-[hsl(var(--gold))] align-middle" />}
              </div>
              {sources.length > 0 && (
                <div className="mt-4" data-testid="oracle-sources">
                  <p className="label-eyebrow flex items-center gap-1"><BookOpenText className="h-3.5 w-3.5" />Grounded in</p>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {sources.map((s, i) => (
                      <div key={`${s.title}-${s.section}`} className="rounded-lg border border-[hsl(var(--gold))]/20 bg-secondary/40 p-3" data-testid={`oracle-source-${i}`}>
                        <p className="font-mono-data text-xs text-[hsl(var(--gold))]">{s.title} §{s.section}</p>
                        <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{s.snippet}…</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </Card>
          )}

          {history.length > 0 && (
            <Card className="gold-ring glass p-5" data-testid="oracle-history">
              <div className="flex items-center gap-2"><History className="h-4 w-4 text-[hsl(var(--water))]" /><h3 className="font-display text-base font-700">Your recent questions</h3></div>
              <ul className="mt-3 divide-y divide-border/60">
                {history.slice(0, 8).map((h) => (
                  <li key={h.id} className="py-2">
                    <button type="button" onClick={() => ask(h.question)} className="w-full text-left text-sm font-600 hover:text-[hsl(var(--gold))]" data-testid={`oracle-history-${h.id}`}>{h.question}</button>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{h.answer}</p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
        <DocsPanel data={docs} onChanged={loadDocs} />
      </div>
    </div>
  );
}
